/**
 * App → app-integration → SQS → live-activity worker: what a phone reports about its Live
 * Activities (spec D11, D13, D15, D17). The app sends the REQUEST shape (voll_users_app#124,
 * `live_activity_event.dart`); app-integration stamps identity (the customer publicId, never the
 * legacy id) and provenance before enqueueing the COMMAND shape. APNs tokens are the lowercase hex of
 * the token bytes; the Android FCM token (D25) is opaque.
 */
import { z } from 'zod';

export const ApnsEnvironmentSchema = z.enum(['sandbox', 'production']);
export type ApnsEnvironment = z.infer<typeof ApnsEnvironmentSchema>;

const DeviceIdSchema = z.string().min(8).max(100);
const ApnsTokenSchema = z.string().regex(/^[0-9a-f]{32,512}$/);
/**
 * The install's FCM registration token (the Android flight card and the chat reply push, on both
 * platforms): one per app install, opaque (`dJ3k…:APA91b…`).
 * Letters, digits, `_`, `-` and `:` — never the APNs hex.
 */
const FcmTokenSchema = z.string().regex(/^[\w:-]{32,4096}$/);
const VoucherIdSchema = z.string().min(1).max(200);
const LegIndexSchema = z.number().int().nonnegative();
/**
 * Raw first device language (`Locale.preferredLanguages.first`: `en-US`, `pt-BR`, `es-419`).
 * Tolerant on purpose: the app forwards what the OS returns, and a 400 would kill the whole
 * command (same lesson as `uiTimeZone`). An odd value is dropped; the worker resolves the rest
 * with `resolveLocale`.
 */
const DeviceLocaleSchema = z.string().max(35).optional().catch(undefined);

const pushToStart = {
  type: z.literal('push_to_start_token'),
  deviceId: DeviceIdSchema,
  token: ApnsTokenSchema,
  apnsEnvironment: ApnsEnvironmentSchema,
  locale: DeviceLocaleSchema,
};
const activityToken = {
  type: z.literal('activity_token'),
  deviceId: DeviceIdSchema,
  voucherId: VoucherIdSchema,
  legIndex: LegIndexSchema,
  token: ApnsTokenSchema,
  apnsEnvironment: ApnsEnvironmentSchema,
  locale: DeviceLocaleSchema,
};
const activityEnded = {
  type: z.literal('activity_ended'),
  deviceId: DeviceIdSchema,
  voucherId: VoucherIdSchema,
  legIndex: LegIndexSchema,
  reason: z.enum(['dismissed', 'ended']),
};
const enablement = {
  type: z.literal('enablement'),
  deviceId: DeviceIdSchema,
  enabled: z.boolean(),
};
/** Android: no per-activity token — one FCM token serves every card of the phone (D25). */
const fcmToken = {
  type: z.literal('fcm_token'),
  deviceId: DeviceIdSchema,
  token: FcmTokenSchema,
  locale: DeviceLocaleSchema,
};
/** The chat reply push (any platform): the install's FCM token, apart from the flight card. */
const chatPush = {
  type: z.literal('chat_push'),
  deviceId: DeviceIdSchema,
  platform: z.enum(['ios', 'android', 'web']),
  token: FcmTokenSchema.nullable(),
  enabled: z.boolean(),
  locale: DeviceLocaleSchema,
};
const serverStamp = {
  version: z.literal(1),
  commandId: z.string().min(1).max(100),
  customerId: z.string().min(1).max(200),
  occurredAt: z.coerce.date(),
};

export const LiveActivityCommandRequestSchema = z.discriminatedUnion('type', [
  z.object(pushToStart).strict(),
  z.object(activityToken).strict(),
  z.object(activityEnded).strict(),
  z.object(enablement).strict(),
  z.object(fcmToken).strict(),
  z.object(chatPush).strict(),
]);
export type LiveActivityCommandRequest = z.infer<typeof LiveActivityCommandRequestSchema>;

export const LiveActivityCommandSchema = z.discriminatedUnion('type', [
  z.object({ ...pushToStart, ...serverStamp }).strict(),
  z.object({ ...activityToken, ...serverStamp }).strict(),
  z.object({ ...activityEnded, ...serverStamp }).strict(),
  z.object({ ...enablement, ...serverStamp }).strict(),
  z.object({ ...fcmToken, ...serverStamp }).strict(),
  z.object({ ...chatPush, ...serverStamp }).strict(),
]);
export type LiveActivityCommand = z.infer<typeof LiveActivityCommandSchema>;
export type LiveActivityCommandInput = z.input<typeof LiveActivityCommandSchema>;
